import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
// Hooks
import { useRefetchOnUserLogIn } from 'hooks';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Api
import { GET_STREAMS } from 'api/streams/queries';
// Helpers
import type { WatchStreamType } from 'helpers/streamFilters';
import { buildStreamFilterInput } from 'helpers/streamFilters';
// Types
import { GetStreams, GetStreamsVariables } from 'api/streams/types/GetStreams';
import { SearchLocation } from 'api/graphql-analytics-global-types';
// Components
import InfiniteScrollLoader from 'ui/InfiniteScrollLoader/InfiniteScrollLoader';
import InfiniteScroll from 'components/common/InfiniteScroll/InfiniteScroll';
import NotFound from 'components/common/NotFound/NotFound';
import StreamsList from 'components/common3/StreamsList/StreamsList';
import PageWithFiltersWrapper from 'components/common3/PageWithFiltersWrapper/PageWithFiltersWrapper';

const PER_PAGE = 16;

type StreamsProps = {
  isPage?: boolean;
  noClear?: boolean;
  storeSlug?: string | undefined | null;
};

export const Streams = ({
  isPage = true,
  noClear = false,
  storeSlug,
}: StreamsProps) => {
  const { filters, sorting, searchKey, search } = useFiltersContext();

  const watchType: WatchStreamType =
    (filters.watchType?.[0] as WatchStreamType) || 'all';

  const inputVariables = useMemo(
    () =>
      buildStreamFilterInput({
        type: watchType,
        priceRange:
          filters.priceRange?.length === 2
            ? [Number(filters.priceRange[0]), Number(filters.priceRange[1])]
            : undefined,
        orderBy: sorting.order,
        direction: sorting.direction,
        streamStatus: filters.streamStatus?.length
          ? filters.streamStatus
          : undefined,
        scheduleDate:
          filters.dateRange?.length === 2
            ? [filters.dateRange[0], filters.dateRange[1]]
            : undefined,
        searchTerm:
          searchKey && search[searchKey]
            ? String(search[searchKey])
            : undefined,
        limit: PER_PAGE,
        offset: 0,
        storeSlug: filters?.athlete?.length
          ? filters.athlete[0]
          : storeSlug ?? undefined,
        storeIds: [
          ...filters.athleteIds,
          ...filters.organizationIds,
          ...filters.contentCreatorIds,
        ].filter(Boolean),
        withCopies: isPage ? false : undefined,
        tzcodes: filters.tzcodes?.length ? filters.tzcodes : undefined,
      }),
    [filters, sorting, searchKey, search, isPage, storeSlug, watchType]
  );
  const { data, previousData, loading, fetchMore, refetch } = useQuery<
    GetStreams,
    GetStreamsVariables
  >(GET_STREAMS, {
    variables: {
      input: {
        ...inputVariables,
      },
      withFilters: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  useRefetchOnUserLogIn({ refetch });

  const displayData = data ?? previousData;
  const streams = displayData?.streamsV2?.entities || [];
  const total = displayData?.streamsV2?.total ?? 0;
  const hasMore = displayData ? streams.length !== total : false;
  const isNotFound = !loading && total === 0;
  const isInitialLoading = loading && !displayData;

  const fetchMoreData = () => {
    const prevOffset = displayData?.streamsV2?.offset || 0;
    const offset = prevOffset ? prevOffset + PER_PAGE : PER_PAGE;

    fetchMore({
      variables: {
        input: {
          ...inputVariables,
          offset,
        },
      },
    });
  };

  return (
    <PageWithFiltersWrapper
      sortKey="Streams"
      title="Live Sports Streams, PPVs and Podcasts"
      // The athlete profile tab renders this same component under its own h1.
      renderTitleAsH1={isPage}
      loading={isInitialLoading}
      filters={displayData?.streamsV2?.filtersV2}
      isPage={isPage}
      noClear={noClear}
      searchBarLocation={SearchLocation.Streams}
      headerSpaceForMobile={false}
      addSmartBannerPaddingToFilters
    >
      <InfiniteScroll
        dataLength={streams.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={<InfiniteScrollLoader />}
        isOverflowVisible
      >
        {isNotFound && <NotFound title="Streams not found" />}
        <StreamsList streams={streams} />
      </InfiniteScroll>
    </PageWithFiltersWrapper>
  );
};

export default Streams;
